import { DeviceSizes } from 'beek-design-system';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { createGlobalStyle } from 'styled-components';
import { useRouter } from 'next/router';
import Avo from 'src/tracking/Avo';
import CommonHead, { Metadata } from '../Common/CommonHead';
import WelcomeHeader from './WelcomeHeader/WelcomeHeader';
import cdn from 'src/common/utils/cdn';
import clsx from 'clsx';
import routeWithOffer from './utils/routeWithOffer';
import styles from './styles.module.css';

export type OnboardingStep =
  | 'Signup'
  | 'Path'
  | 'Library'
  | 'Goal'
  | 'Personalizing'
  | 'Review'
  | 'Subscribe'
  | 'ChallengeOnboarding';

type LayoutProps = {
  currentStep: OnboardingStep;
  children: ReactNode;
  previousStep?: OnboardingStep;
  hasBackButton?: boolean;
  footerButtons?: JSX.Element[];
};

// What percent of the progress bar each step should fill
const percentLookup: Record<OnboardingStep, number> = {
  Signup: 0,
  Path: 20,
  Library: 40,
  Personalizing: 60,
  Goal: 80,
  Review: 100,
  Subscribe: 100,
  ChallengeOnboarding: 40,
};

// Tells the back button which page to navigate back to, if present
export const urlLookup: Record<OnboardingStep, string> = {
  Signup: '/signup',
  Path: '/welcome/choose-your-path',
  Library: '/welcome/fill-your-library',
  Personalizing: '/welcome/personalizing',
  Goal: '/welcome/set-your-goal',
  Review: '/welcome/review',
  Subscribe: '/subscription/start',
  ChallengeOnboarding: '/welcome/challenge-onboarding',
};

const getMetadata = (): Metadata => {
  const metadata = {
    title: 'Bienvenidos',
    author: 'Beek',
    orgAuthor: 'Beek',
    description:
      'Descarga, escucha y aprende sin cambiar tu rutina con los mejores expertos en finanzas, salud, bienestar, auto ayuda y más.',
    date: '2022-07-07',
    category: 'Audiobooks',
    cover: cdn(
      'image/landing_page/beek-meta-image.webp?width=1200&height=630&quality=75&format=jpg'
    ),
    url: `https://beek.io`,
    tags: 'Beek',
  };

  return metadata;
};

// Have to override the global styled-components to scale only the welcome pages
const GlobalStyleOverride = createGlobalStyle`
 html {
    font-size: 12px;
    @media ${DeviceSizes.mobileS} {
      font-size: 14px;
    }
    @media ${DeviceSizes.mobileM} {
      font-size: 16px;
    }
    @media ${DeviceSizes.mobileL} {
      font-size: 16px;
    }
    @media ${DeviceSizes.tablet} {
      font-size: 18px;
    }
    @media ${DeviceSizes.laptop} {
      font-size: 20px;
    }
    @media ${DeviceSizes.laptopL} {
      font-size: 20px;
    }
    @media ${DeviceSizes.desktop} {
      font-size: 32px;
    }
    @media ${DeviceSizes.desktopL} {
      font-size: 36px;
    }
    @media screen and (min-width: 3840px) {
      font-size: 48px;
    }
  }`;

const Layout = ({
  currentStep,
  children,
  previousStep,
  hasBackButton,
  footerButtons,
}: LayoutProps): JSX.Element => {
  const router = useRouter();
  const metadata = getMetadata();

  // Need to calculate scroll percent to dynamically hide scroll overflow shadow.
  const [scrollPercent, setScrollPercent] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  const handleScroll = useCallback(() => {
    if (document.body.parentNode) {
      // @ts-ignore
      const { scrollTop, scrollHeight, clientHeight } =
        document.body.parentNode;
      const percent = Math.round(
        (scrollTop / (scrollHeight - clientHeight)) * 100
      );
      setScrollPercent(percent);
      setIsScrollable(scrollTop > 0);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const onBackButtonClick = useCallback(() => {
    Avo.onboardingBackButtonPressed({
      origin: `Onboarding Step: ${currentStep}`,
      connectionType: navigator.connection?.type ?? 'unknown',
      isOnline: navigator.onLine,
    });

    // TODO: Remove this once Avo pipeline is correctly diagnosed.
    Avo.gtmTestingEvent();

    if (previousStep) {
      routeWithOffer(previousStep, router);
    } else {
      router.back();
    }
  }, [currentStep, previousStep, router]);

  return (
    <>
      <CommonHead metadata={metadata} />
      <GlobalStyleOverride />
      <div className={styles.container}>
        <WelcomeHeader
          percentComplete={percentLookup[currentStep]}
          hasBackButton={hasBackButton}
          onBackButtonClick={onBackButtonClick}
        />
        <div className={styles.children}>{children}</div>
        {footerButtons && footerButtons.length && (
          <footer className={styles.footer}>
            <div
              className={clsx(styles.gradient, {
                [styles.hide]: !isScrollable || scrollPercent === 100,
              })}
            />
            <div className={styles.buttonContainer}>
              {footerButtons.map((button, index) => (
                <div
                  key={`${button.key + index.toString()}`}
                  className={styles.button}
                >
                  {button}
                </div>
              ))}
            </div>
          </footer>
        )}
      </div>
    </>
  );
};

export default Layout;
